import { Text, Tooltip } from '@wix/design-system';
import { useEclipsePlanRecommendation } from '@wix/package-picker-shared-eclipse';
import { Confirm } from '@wix/wix-ui-icons-common/lazy';
import classNames from 'classnames';
import React from 'react';
import { EclipsePackagePickerModule } from '../../modules/eclipse-package-picker.module';
import { DATA_HOOKS } from './constants';
import { AccordionIcon, SparkleIcon } from './AiPlanRecommendationCard.icons';
import s from './AiPlanRecommendationCard.scss';

export const AiPlanRecommendationCard: React.FC = () => {
  const { t } = EclipsePackagePickerModule.internals.useTranslations();
  const {
    recommendedProductId,
    recommendedProductName,
    siteName,
    bullets,
    isError,
    isEnabled,
  } = useEclipsePlanRecommendation();
  const isCollapsed =
    EclipsePackagePickerModule.SDK.aiRecommendationStore.useIsCollapsed();

  const title = siteName
    ? t('premium-purchase-plan.eclipse.ai-recommendation.title-for-name', {
        planName: recommendedProductName,
        siteName,
      })
    : t('premium-purchase-plan.eclipse.ai-recommendation.title-for-site', {
        planName: recommendedProductName,
      });

  const aiText = [title, ...bullets].filter(Boolean).join(' | ');

  const reportAiEvent = React.useCallback(
    (action: 'load' | 'view' | 'click', clickType?: 'chip' | 'close') => {
      const report =
        EclipsePackagePickerModule.internals.bi?.onAiRecommendationComponent;
      if (!report) {
        return false;
      }
      report({
        action,
        clickType,
        aiText,
        recommendation: recommendedProductName,
        productId: recommendedProductId,
      });
      return true;
    },
    [aiText, recommendedProductName, recommendedProductId],
  );

  const handleToggle = React.useCallback(() => {
    const store = EclipsePackagePickerModule.SDK.aiRecommendationStore;
    if (isCollapsed) {
      reportAiEvent('click', 'chip');
      store.expand();
    } else {
      reportAiEvent('click', 'close');
      store.collapse();
    }
  }, [isCollapsed, reportAiEvent]);

  const hasLoadedRef = React.useRef(false);
  React.useEffect(() => {
    if (bullets.length && !hasLoadedRef.current && reportAiEvent('load')) {
      hasLoadedRef.current = true;
    }
  }, [bullets.length, reportAiEvent]);

  const hasViewedRef = React.useRef(false);
  React.useEffect(() => {
    if (!hasViewedRef.current && reportAiEvent('view')) {
      hasViewedRef.current = true;
    }
  }, [reportAiEvent]);

  if (isError || !isEnabled || !recommendedProductId) {
    return null;
  }

  const ariaLabel = t(
    'premium-purchase-plan.eclipse.ai-recommendation.aria-label',
  );
  const toggleLabel = isCollapsed
    ? t('premium-purchase-plan.eclipse.ai-recommendation.expand')
    : t('premium-purchase-plan.eclipse.ai-recommendation.collapse');

  return (
    <div
      className={s.card}
      data-hook={DATA_HOOKS.root}
      role="region"
      aria-label={ariaLabel}
    >
      <div className={s.headerRow}>
        <div className={s.headerLeft}>
          <SparkleIcon svgClassName={s.headerSparkle} />
          <Text
            size="small"
            weight="bold"
            className={s.insightText}
            dataHook={DATA_HOOKS.title}
          >
            {title}
          </Text>
        </div>
        <Tooltip className={s.tooltip} content={toggleLabel}>
          <button
            type="button"
            className={s.toggle}
            data-hook={DATA_HOOKS.toggle}
            aria-label={toggleLabel}
            aria-expanded={!isCollapsed}
            aria-controls={DATA_HOOKS.body}
            onClick={handleToggle}
          >
            <AccordionIcon
              svgClassName={classNames(s.chevron, {
                [s.chevronDown]: isCollapsed,
              })}
            />
          </button>
        </Tooltip>
      </div>

      {!isCollapsed && (
        <div
          className={s.body}
          id={DATA_HOOKS.body}
          data-hook={DATA_HOOKS.body}
        >
          <ul className={s.bullets}>
            {bullets.map((bullet, index) => (
              <li
                key={`${index}-${bullet}`}
                className={classNames(s.bullet, s.fadeIn)}
              >
                <span className={s.bulletIcon} aria-hidden>
                  <Confirm width={16} height={16} />
                </span>
                <Text size="small" weight="thin" className={s.bulletText}>
                  {bullet}
                </Text>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
